import {useCallback, useMemo} from "react";
import {useTranslation} from "react-i18next";
import {FilterTypes} from "../constans/filter";
import {useDetailsFormatter} from "../hooks/useDetailsFormatter";
import {getDetailsFilterType} from "../utils/DashboardUtils";
import {createQueryForCustomComponentDetails} from "../utils/SQLUtils";
import {createKeyToValueDictionary} from "../utils/Utils";
import useQueryCallerResource from "./useQueryCallerResource";

export function useDashboardDetails(
    item,
    filterValue,
    filterItem,
    keysDictionary
) {
    const {t} = useTranslation();

    const parameterDictionary = useMemo(
        () => createKeyToValueDictionary(item.Parameters, "id"),
        [item.Parameters]
    );

    const query = useMemo(() => {
        const {Details, Parameters, Filter, Options, Group} = item;
        return createQueryForCustomComponentDetails(
            Parameters,
            Group,
            Filter,
            Options,
            filterItem,
            filterValue,
            Details
        );
    }, [filterItem, filterValue, item]);

    const resource = useQueryCallerResource(query);

    const getFormatter = useDetailsFormatter();

    const getFilter = useCallback(
        (detail) => {
            switch (detail) {
                case "AnmID":
                case "inseminationBoar":
                    return FilterTypes.STRING;
                case "group_nr":
                    return FilterTypes.TECHNOLOGY_GROUP;
                case "inseminationDate":
                case "usgDate":
                case "noPregnancy":
                    return FilterTypes.DATE;
                case "usgResult":
                    return FilterTypes.USG_RESULT;
                case "plcmntID":
                    return FilterTypes.LOCATION;
                case "race":
                    return FilterTypes.RACE;
                default: {
                    const parameter = parameterDictionary[detail];
                    if (!parameter || parameter.parameter === "MATH")
                        return FilterTypes.NUMBER;
                    return getDetailsFilterType(parameter, keysDictionary);
                }
            }
        },
        [parameterDictionary, keysDictionary]
    );

    const getName = useCallback(
        (detail, parameter) => {
            switch (detail) {
                case "AnmID":
                    return t("animal");
                case "cycle":
                    return t("printSelectedAnimalsModal.cycle");
                case "group_nr":
                    return t("group");
                case "inseminationDate":
                    return t("inseminationDate");
                case "inseminationBoar":
                    return t("animalTypes.4");
                case "usgDate":
                    return t("usgDate");
                case "usgResult":
                    return t("usgResult");
                case "bornAlive":
                    return t("liveBorn");
                case "bornDead":
                    return t("deadBorn");
                case "bornMummy":
                    return t("mummyBorn");
                case "bornWeak":
                    return t("weakBorn");
                case "bornGilt":
                    return t("giltsBorn");
                case "fallPiglets":
                    return t("planTypes.numberOfFallenPigs");
                case "fallPigletsAge":
                    return t("deadPigletAge");
                case "fostering":
                    return t("controlLists.mainView.fosteringTitle");
                case "weighting":
                    return t("pigletsWeighting");
                case "separation":
                    return t("separatedPigletsCount");
                case "activeNipples":
                    return t("activeNipples");
                case "noPregnancy":
                    return t("eventTypes.N");
                case "plcmntID":
                    return t("events.transferEvent.actualLocation");
                case "race":
                    return t("race");
                case "raceCode":
                    return t("raceCode");
                case "idleDays":
                    return t("sterilleDays");
                default:
                    if (detail.endsWith("_Date"))
                        return `[${t("date")}] ${parameter?.parameterName}`;
                    if (detail.endsWith("_Dictionary"))
                        return `[${t("singleDictionary")}] ${parameter?.parameterName}`;
                    if (detail.endsWith("_PlcmntID"))
                        return `[${t("location")}] ${parameter?.parameterName}`;
                    if (detail.endsWith("_GroupDay")) {
                        return `[${t("dayInGroup")}] ${parameter?.parameterName}`;
                    }
                    if (detail.endsWith("_Day")) {
                        return `[${t("day")}] ${parameter?.parameterName}`;
                    }
                    if (detail.endsWith("_Month")) {
                        return `[${t("month")}] ${parameter?.parameterName}`;
                    }
                    if (detail.endsWith("_Week")) {
                        return `[${t("week")}] ${parameter?.parameterName}`;
                    }
                    if (detail.endsWith("_Year")) {
                        return `[${t("year")}] ${parameter?.parameterName}`;
                    }
                    if (detail.endsWith("_CycleDay")) {
                        return `[${t("animalCycleDay")}] ${parameter?.parameterName}`;
                    }
                    if (detail.endsWith("_CycleWeek")) {
                        return `[${t("cycleWeek")}] ${parameter?.parameterName}`;
                    }
                    if (detail.endsWith("_Cycle")) {
                        return `[${t("cycle")}] ${parameter?.parameterName}`;
                    }
                    if (detail.endsWith("_User")) {
                        return `[${t("user")}] ${parameter?.parameterName}`;
                    }
                    return `[${t("series")}] ${parameter?.parameterName}`;
            }
        },
        [t]
    );

    const getParameter = useCallback(
        (detail) => {
            const tmp = detail.split("_");
            return parameterDictionary[tmp[0]];
        },
        [parameterDictionary]
    );

    const getFilterColumn = useCallback((detail, parameter) => {
        if (detail === "AnmID") return "AnmNo1";
        if (!parameter) return undefined;
        return parameter.id?.replaceAll(".", "_");
    }, []);

    const getHeader = useCallback(
        (detail, parameter) => {
            const formatter = getFormatter(
                detail,
                parameter,
                keysDictionary[parameter?.parameter]
            );
            const isComponent = [
                "AnmID",
                "inseminationBoar",
                "raceCode",
            ].includes(detail);
            const ignoreValueFormatterFilter = [
                "group_nr",
                "inseminationDate",
                "usgDate",
                "noPregnancy",
                "usgResult",
                "plcmntID",
            ].includes(detail);
            return {
                name: getName(detail, parameter),
                field: detail,
                valueFormatter: !isComponent ? formatter : null,
                component: isComponent ? formatter : null,
                filterType: getFilter(detail),
                disableValueFormatterFilter: ignoreValueFormatterFilter,
                filterColumn: getFilterColumn(detail, parameter),
            };
        },
        [getFormatter, getFilter, getName, getFilterColumn, keysDictionary]
    );

    const getChildren = useCallback(
        (parameter) => {
            if (parameter?.parameter !== "MATH") return null;
            const regex = new RegExp("\\[[^\\]]+\\]", "g");
            const match = parameter.math.match(regex);
            if (!match) return null;
            return match.map((item) => {
                const name = item.slice(1, -1);
                const parameter = getParameter(name);
                return getHeader(name, parameter);
            });
        },
        [getHeader, getParameter]
    );

    const headers = useMemo(() => {
        return item.Details.map((detail) => {
            const parameter = getParameter(detail);
            console.log("PARAM", parameter);
            return {
                ...getHeader(detail, parameter),
                children: getChildren(parameter),
            };
        });
    }, [item, getHeader, getParameter, getChildren]);

    return {resource, headers};
}
